import React, { useCallback, useEffect, useRef, useState } from "react";
import AttendanceRoster, { RosterMark, RosterRow, SaveResult } from "../attendance/AttendanceRoster";
import { RosterAction, RosterStatus } from "../attendance/attendanceContexts";
import { useAttendanceLive } from "../../hooks/useAttendanceLive";
import {
  clearStaffAttendance,
  getStaffRoster,
  getStaffRosterFlags,
  KioskProgram,
  KioskSlot,
  KioskStaffDay,
  KioskStaffRosterEntry,
  markStaffAttendance,
} from "../../api/kioskAPI";
import { formatDateOnly } from "@mediatechgroup/calendar-date";
import { captureKioskError } from "../../utils/sentry";
import "./KioskStaffRoster.css";

/**
 * STAFF KIOSK MODE — the class roster on the kiosk.
 *
 * This is the staff class roster (components/attendance/AttendanceRoster in
 * the `class` context) with a kiosk `save`: every mark and clear goes to the
 * backend's /kiosk/staff/attendance/* routes, which run the SAME handlers as
 * the staff attendance page, with the KIOSK token. The screen stays on the
 * roster after every mark (staff mark a whole class; self check-in's "tap and
 * return to the program list" does not apply here).
 *
 * Marks are recorded as the kiosk user — that trade-off is what the tenant
 * accepted when it turned staff mode on (Settings → Kiosk).
 */

interface KioskStaffRosterProps {
  program: KioskProgram;
  slot: KioskSlot;
  /** The tenant calendar day the backend listed this class for (YYYY-MM-DD). */
  date: string;
  day: KioskStaffDay;
  onBack: () => void;
}

const ITEMS_PER_PAGE = 50;

function toRow(entry: KioskStaffRosterEntry, isTrial = false): RosterRow {
  return {
    id: entry.athleteId,
    firstName: entry.firstName,
    lastName: entry.lastName,
    isTrial: isTrial || !!entry.isTrial,
    onVacationHold: !!entry.onVacationHold,
    vacationReturnDate: entry.vacationReturnDate ?? null,
  };
}

const KioskStaffRoster: React.FC<KioskStaffRosterProps> = ({ program, slot, date, day, onBack }) => {
  const [rows, setRows] = useState<RosterRow[]>([]);
  const [attendance, setAttendance] = useState<Record<string, RosterMark | undefined>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const readSeq = useRef(0);

  // `quiet`: a live re-read — no spinner, rows update in place. Only the newest
  // read may land.
  const load = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      const seq = ++readSeq.current;
      if (!quiet) setLoading(true);
      try {
        const res = await getStaffRoster(program.id, date, slot.id);
        if (seq !== readSeq.current) return;
        const enrolled = (res?.data || []).filter(
          (e) => e?.isEnrolled && (e?.athleteStatus || "active") === "active"
        );
        const trials = res?.trialAttendees || [];
        setRows([...enrolled.map((e) => toRow(e)), ...trials.map((t) => toRow(t, true))]);
        // Only explicitly saved marks (they carry an id) — no default "absent".
        const marks: Record<string, RosterMark> = {};
        [...(res?.data || []), ...trials].forEach((e) => {
          if (e?.id || e?.attendance_id) marks[e.athleteId] = { status: e.status };
        });
        setAttendance(marks);
        setError(null);
      } catch (err: unknown) {
        if (seq !== readSeq.current) return;
        captureKioskError(err instanceof Error ? err : new Error(String(err)), "getStaffRoster");
        const e = err as { response?: { data?: { error?: string } } };
        setError(e.response?.data?.error || "Unable to load this class. Please try again.");
      } finally {
        if (seq === readSeq.current) setLoading(false);
      }
    },
    [program.id, date, slot.id]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Another screen's mark on this class sheet (the staff page, another kiosk)
  // pings it; re-read quietly. Same public, data-free broadcast the staff
  // page listens on.
  useAttendanceLive({ context: "class", slotId: slot.id, date }, () => load({ quiet: true }));

  const updateAttendanceLocally = (rowId: string, status: RosterStatus) => {
    setAttendance((prev) => ({ ...prev, [rowId]: { status } }));
  };

  const save = async (athleteId: string, action: RosterAction, row: RosterRow): Promise<SaveResult> => {
    const where = { programId: program.id, timeSlotId: slot.id, attendanceDate: date, athleteId };
    if (action === "clear") return clearStaffAttendance(where);
    if (action !== "present" && action !== "absent") return { ok: false };
    return markStaffAttendance({ ...where, status: action, isTrial: !!row?.isTrial });
  };

  const dayWord = day === "yesterday" ? "Yesterday" : "Today";
  const dateLabel = formatDateOnly(date, undefined, { weekday: "long", month: "long", day: "numeric" });

  return (
    <div className="kiosk-screen kiosk-staff-roster">
      <div className="kiosk-header">
        <button className="kiosk-header__back" onClick={onBack}>
          <span className="kiosk-header__back-icon">&#8249;</span>
          Back
        </button>
        <div className="kiosk-header__title">
          <h1>{program.label}</h1>
          <p>
            {slot.time ? `${slot.time} · ` : ""}
            <span className="kiosk-staff-roster__day" data-testid="kiosk-staff-roster-day">
              {dayWord}
            </span>
            {dateLabel ? `, ${dateLabel}` : ""}
          </p>
        </div>
        <div className="kiosk-header__clock-wrapper" />
      </div>

      <div className="kiosk-staff-roster__body bg-white dark:bg-gray-900">
        <p className="kiosk-staff-roster__note">
          Staff attendance — marks save as you tap and are recorded as “Kiosk”. Tap a mark again to clear it.
        </p>
        {loading ? (
          <div className="kiosk-loading">
            <div className="kiosk-loading__spinner" />
            <p>Loading class...</p>
          </div>
        ) : error ? (
          <div className="kiosk-empty">
            <p role="alert">{error}</p>
            <button className="kiosk-retry-btn" onClick={() => load()}>
              Try Again
            </button>
          </div>
        ) : (
          <AttendanceRoster
            context="class"
            rows={rows}
            attendance={attendance}
            updateAttendanceLocally={updateAttendanceLocally}
            save={save}
            renderName={(row) => (
              <span className="font-medium">
                {[row.firstName, row.lastName].filter(Boolean).join(" ")}
                {row.isTrial && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                    Trial
                  </span>
                )}
                {row.onVacationHold && (
                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800">
                    Hold
                  </span>
                )}
              </span>
            )}
            // Medical alert / no photos / waiver missing, the same badges as
            // the staff rosters, read with the kiosk sign-in. A trial attendee
            // has no athlete record to check.
            flagSubject={(row) => (row?.isTrial || !row?.id ? null : { athleteId: row.id })}
            flagFetcher={getStaffRosterFlags}
            itemsPerPage={ITEMS_PER_PAGE}
            emptyText="No athletes are enrolled in this class."
          />
        )}
      </div>
    </div>
  );
};

export default KioskStaffRoster;
